import { useCallback, useEffect, useRef, useState } from 'react';

export type Carga<T> =
  { estado: 'cargando' } | { estado: 'listo'; datos: T } | { estado: 'error'; mensaje: string };

/**
 * 🔴 PUNTO DE INTEGRACION — este hook es el hueco donde entra el `fetch` real.
 *
 * Hoy entrega los datos de ejemplo despues de un instante. Ese instante no es
 * un adorno: hace que el estado de carga de cada pantalla se dibuje de verdad
 * en cada visita. Un estado de carga que nunca se ve es un estado de carga que
 * nadie sabe si funciona.
 *
 * Cuando exista `apps/api`, este archivo pasa a hacer la llamada y devuelve los
 * mismos tres estados. Ninguna pantalla cambia: todas ya estan escritas para
 * mostrar carga, error y vacio.
 *
 * ⚠️ CORRE UNA SOLA VEZ, AL MONTAR, y esa decision tiene motivo.
 *   La version anterior llevaba `datos` en las dependencias del efecto. Una
 *   pantalla que le pasa un objeto armado en el render (`{ a, b }`) le da una
 *   referencia NUEVA cada vez => el efecto se volvia a disparar, volvia a poner
 *   "cargando", y la pantalla quedaba cargando para siempre. Paso de verdad en
 *   el panel, y no lo caza el compilador: se ve recien al abrir la pantalla.
 *   Con la referencia guardada y el efecto sin dependencias, da igual como se
 *   arme el argumento.
 *   Al reemplazarlo por el `fetch` real, las dependencias tienen que ser los
 *   PARAMETROS de la consulta (filtros, pagina), nunca el objeto de datos.
 *
 * ⚠️ El estado `error` existe y hoy NO se puede producir desde la interfaz.
 * Esta escrito y conectado en todas las pantallas para que el dia que el
 * backend falle haya algo que mostrar, pero mientras no haya backend nadie
 * verifico que se vea bien con un fallo real.
 */
export function useCargaDeEjemplo<T>(datos: T, demoraMs = 220): Carga<T> {
  const [carga, setCarga] = useState<Carga<T>>({ estado: 'cargando' });

  const ultimos = useRef(datos);
  ultimos.current = datos;

  useEffect(() => {
    const t = window.setTimeout(
      () => setCarga({ estado: 'listo', datos: ultimos.current }),
      demoraMs,
    );
    return () => window.clearTimeout(t);
  }, [demoraMs]);

  return carga;
}

/**
 * La versión REAL de `useCargaDeEjemplo`, para las pantallas que ya tienen a quién llamarle
 * (`Verificacion.tsx`, `Dispatch.tsx`). No reemplaza al hook de arriba — las pantallas que
 * siguen en modo "datos de ejemplo" (`Panel`, `Conductores`, `Viajes`, etc.) no lo tocan.
 *
 * Mismos tres estados, mismo motivo para no depender de un objeto de datos en el efecto: acá
 * `cargar` es una FUNCIÓN, así que la referencia cambia en cada render igual que pasaba con el
 * objeto — se guarda en un `ref` y el efecto sólo reacciona a `deps` (los PARÁMETROS de la
 * consulta: filtros, un id seleccionado, nunca el resultado).
 *
 * Devuelve `[estado, recargar]`: `recargar` fuerza una llamada nueva sin esperar a que cambien
 * los `deps` — es lo que usa el botón "Reintentar" de `ConError`, y también lo que una pantalla
 * llama después de una mutación exitosa (aprobar/rechazar), para que la fila que cambió de
 * estado no quede mostrando el dato viejo hasta el próximo remount.
 */
export function useCargaReal<T>(
  cargar: () => Promise<T>,
  deps: readonly unknown[],
): [Carga<T>, () => void] {
  const [carga, setCarga] = useState<Carga<T>>({ estado: 'cargando' });
  const [version, setVersion] = useState(0);
  const cargarRef = useRef(cargar);
  cargarRef.current = cargar;

  useEffect(() => {
    let cancelado = false;
    setCarga({ estado: 'cargando' });

    cargarRef
      .current()
      .then((datos) => {
        if (!cancelado) setCarga({ estado: 'listo', datos });
      })
      .catch((error: unknown) => {
        if (cancelado) return;
        const mensaje =
          error instanceof Error && error.message
            ? error.message
            : 'Ocurrió un error inesperado al pedirle datos al backend.';
        setCarga({ estado: 'error', mensaje });
      });

    return () => {
      cancelado = true;
    };
    // `deps` la controla quien llama al hook (son los parámetros de SU consulta); `version` es
    // el disparador interno de `recargar`. No hay forma de declarar esto como lista estática
    // porque `deps` es del tamaño que decida el llamador — mismo patrón que useCargaDeEjemplo
    // resuelve con un ref, un nivel más arriba. (Sin plugin `react-hooks` en este proyecto: no
    // hay `exhaustive-deps` que satisfacer ni que deshabilitar.)
  }, [...deps, version]);

  const recargar = useCallback(() => setVersion((v) => v + 1), []);

  return [carga, recargar];
}

/**
 * Estado de una pantalla que se REFRESCA sola. No es `Carga<T>` con un timer encima, y la
 * diferencia está en el estado `listo`:
 *
 * ⚠️ **`useCargaReal` vuelve a `{estado:'cargando'}` en cada recarga.** Para un botón
 * "Reintentar" eso está bien; para un poleo cada 5 s convertiría la pantalla en un parpadeo
 * permanente entre el mapa y el esqueleto de carga.
 *
 * 🔴 **Y `falloRefresco` existe por algo peor que la estética.** Si el backend deja de responder
 * y la pantalla siguiera mostrando las últimas posiciones buenas en silencio, el operador
 * despacharía sobre un mapa muerto **que se ve exactamente igual que uno vivo** — el error hacia
 * el silencio. Los datos se conservan (tirarlos sería peor: dejaría al operador sin nada), pero
 * el fallo viaja al lado y la pantalla tiene que mostrarlo. `actualizadoAt` es el complemento:
 * dice DE CUÁNDO son los datos que se están viendo.
 */
export type CargaViva<T> =
  | { estado: 'cargando' }
  /** Nunca llegó nada. Distinto de "llegó y después dejó de refrescar", que es `listo` con fallo. */
  | { estado: 'error'; mensaje: string }
  | { estado: 'listo'; datos: T; actualizadoAt: Date; falloRefresco: string | null };

/**
 * Carga real + refresco periódico, para las pantallas que muestran estado VIVO.
 *
 * `periodoMs <= 0` desactiva el refresco (carga única). No es un caso teórico: `entorno.ts`
 * documenta que una variable mal puesta puede volverse `0`, y un `setInterval(f, 0)` sería un
 * bucle de peticiones tan rápido como responda el backend.
 *
 * 🔑 **No se solapan peticiones.** Si el backend tarda más que el período, el tick que cae
 * mientras hay una en vuelo se descarta en vez de encolar otra: bajo latencia alta, encolar es
 * exactamente lo que convierte una lentitud en una avalancha.
 */
export function useCargaViva<T>(cargar: () => Promise<T>, periodoMs: number): CargaViva<T> {
  const [carga, setCarga] = useState<CargaViva<T>>({ estado: 'cargando' });
  const cargarRef = useRef(cargar);
  cargarRef.current = cargar;

  useEffect(() => {
    let cancelado = false;
    let enVuelo = false;

    const traer = async (): Promise<void> => {
      if (enVuelo) return;
      enVuelo = true;
      try {
        const datos = await cargarRef.current();
        if (cancelado) return;
        setCarga({ estado: 'listo', datos, actualizadoAt: new Date(), falloRefresco: null });
      } catch (error: unknown) {
        if (cancelado) return;
        const mensaje =
          error instanceof Error && error.message
            ? error.message
            : 'Ocurrió un error inesperado al pedirle datos al backend.';
        // Si ya había datos se CONSERVAN y el fallo va al lado; si no había, es error a secas.
        setCarga((previo) =>
          previo.estado === 'listo'
            ? { ...previo, falloRefresco: mensaje }
            : { estado: 'error', mensaje },
        );
      } finally {
        enVuelo = false;
      }
    };

    void traer();
    const timer = periodoMs > 0 ? window.setInterval(() => void traer(), periodoMs) : null;

    return () => {
      cancelado = true;
      if (timer !== null) window.clearInterval(timer);
    };
  }, [periodoMs]);

  return carga;
}
